import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Group,
  Button,
  Box,
  Menu,
  ActionIcon,
  Badge,
  Anchor,
} from '@mantine/core';
import { IconUser, IconPlus, IconCheck, IconX } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { useHealthQuery } from '../../hooks/useHealthQuery';
import { IS_DEVELOPMENT } from '../../constants';

/** Navbar uses useHealthQuery (TanStack Query) for server status: shared cache, no context, polling via refetchInterval. */
const Navbar: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  // Server status indicator: serverUp and loading (hook's isLoading) from useHealthQuery
  // isLoading: loading is destructured with renaming: take the property isLoading from that object and use it in this
  // component as the variable loading.
  const { serverUp, isLoading: loading } = useHealthQuery();
  const navigate = useNavigate();
  const [menuOpened, setMenuOpened] = React.useState(false);

  const handleLogout = () => {
    logout();
    setMenuOpened(false);
    navigate('/login');
  };

  const handleProfile = () => {
    setMenuOpened(false);
    // Navigate to profile page when implemented
  };

  return (
    <Box
      style={{
        height: 60,
        backgroundColor: 'var(--mantine-color-body)',
        borderBottom: '1px solid var(--mantine-color-default-border)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
      }}
    >
      <Group h="100%" px="md" justify="space-between">
        <Anchor
          component={RouterLink}
          to={isAuthenticated ? '/trips' : '/'}
          data-cy="app-title"
          underline="never"
          c="inherit"
          fw={700}
          size="lg"
        >
          TravelBuddy
        </Anchor>
        {/* TODO: Add a logo here */}

        {/* Server Status Indicator - Only show in development environment */}
        {IS_DEVELOPMENT && (
          <Box>
            <Badge
              leftSection={
                loading ? undefined : serverUp ? (
                  <IconCheck size={12} />
                ) : (
                  <IconX size={12} />
                )
              }
              variant="outline"
              size="sm"
              tt="none"
            >
              {loading ? 'CHECKING...' : serverUp ? 'SERVER UP' : 'SERVER DOWN'}
            </Badge>
          </Box>
        )}

        {isAuthenticated ? (
          <Group>
            {/* TODO: Switch to IconButton for My Trips */}
            <Button
              variant="subtle"
              component={RouterLink}
              to="/trips"
              color="gray"
              data-cy="nav-my-trips"
            >
              My Trips
            </Button>
            {/* TODO: Switch to IconButton for New Trip */}
            <Button
              variant="subtle"
              component={RouterLink}
              to="/trips/new"
              leftSection={<IconPlus size={16} />}
              color="gray"
              data-cy="nav-new-trip"
            >
              New Trip
            </Button>

            <Menu
              opened={menuOpened}
              onChange={setMenuOpened}
              position="bottom-end"
              shadow="md"
              width={200}
            >
              <Menu.Target>
                <ActionIcon
                  size="lg"
                  variant="subtle"
                  color="gray"
                  aria-label="User menu"
                  data-cy="user-menu"
                >
                  <IconUser size={20} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item onClick={handleProfile}>
                  Profile: {user?.name}
                </Menu.Item>
                <Menu.Item
                  onClick={handleLogout}
                  color="red"
                  data-cy="nav-logout"
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        ) : (
          <Group>
            <Button
              variant="subtle"
              component={RouterLink}
              to="/login"
              color="gray"
              data-cy="nav-login"
            >
              Login
            </Button>
            <Button
              variant="filled"
              component={RouterLink}
              to="/signup"
              data-cy="nav-signup"
            >
              Sign Up
            </Button>
          </Group>
        )}
      </Group>
    </Box>
  );
};

export default Navbar;
